import React, { useState, useCallback } from "react";
import {
  Box,
  Button,
  Grid,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@material-ui/core";
import { useSelector } from "react-redux";
import useCurrentFacility from "../../hooks/useCurrentFacility";
import { useEnqueueSnackbar } from "../../hooks/useEnqueueSnackbar";
import RewardGroupService from "../../services/RewardGroupService";
import apiClient from "../../auth/apiClient";
import useRewardGroups from "./useRewardGroups";
import RewardGroupsList from "./RewardGroupsList";
import RewardGroupDrawer from "./RewardGroupDrawer";
import { useStyles } from "./styles";
import clsx from "clsx";

const flattenErrorMessages = (value) => {
  if (typeof value === "string") return value.trim() ? [value] : [];
  if (Array.isArray(value)) {
    return value.reduce(
      (messages, item) => messages.concat(flattenErrorMessages(item)),
      []
    );
  }
  if (value && typeof value === "object") {
    return Object.values(value).reduce(
      (messages, item) => messages.concat(flattenErrorMessages(item)),
      []
    );
  }
  return [];
};

export const getRewardGroupErrorMessage = (
  error,
  fallback = "Failed to save reward group."
) => {
  const data = error?.response?.data;
  if (typeof data === "string" && data.trim()) return data;

  const validationMessages = flattenErrorMessages(
    data?.validationErrors || data?.errors
  );
  if (validationMessages.length > 0) return validationMessages.join(" ");

  if (typeof data?.detail === "string" && data.detail.trim()) {
    return data.detail;
  }
  if (typeof data?.message === "string" && data.message.trim()) {
    return data.message;
  }
  if (typeof data?.title === "string" && data.title.trim()) {
    return data.title;
  }
  if (typeof error?.message === "string" && error.message.trim()) {
    return error.message;
  }
  return fallback;
};

const service = new RewardGroupService(apiClient);

const RewardGroupsContainer = () => {
  const classes = useStyles();
  const { facilityID } = useCurrentFacility();
  const enqueueSnackbar = useEnqueueSnackbar();

  const scopeAwareFacilityID = useSelector(
    (state) => state.entityScope?.facilityGroupId || facilityID
  );

  const {
    data,
    loading,
    error,
    pagination,
    setPagination,
    refetch,
  } = useRewardGroups(scopeAwareFacilityID);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("add");
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [accessGroups, setAccessGroups] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [signupCodeServerError, setSignupCodeServerError] = useState("");
  const [emailDomainServerError, setEmailDomainServerError] = useState("");
  const fetchAccessGroups = useCallback(async () => {
    try {
      const response = await service.getEligibleAccessGroups(scopeAwareFacilityID);
      setAccessGroups(response.data || []);
    } catch {
      enqueueSnackbar("Failed to load access groups.", {
        variant: "error",
        tag: "RewardGroupsAccessGroupsError",
      });
    }
  }, [scopeAwareFacilityID, enqueueSnackbar]);

  const handleAdd = async () => {
    await fetchAccessGroups();
    setSignupCodeServerError("");
    setEmailDomainServerError("");
    setDrawerMode("add");
    setSelectedGroup(null);
    setDrawerOpen(true);
  };

  const handleEdit = async (row) => {
    try {
      await fetchAccessGroups();
      const response = await service.getRewardGroup(
        scopeAwareFacilityID,
        row.rewardGroupID
      );
      setSelectedGroup(response.data);
      setSignupCodeServerError("");
      setEmailDomainServerError("");
      setDrawerMode("edit");
      setDrawerOpen(true);
    } catch {
      enqueueSnackbar("Failed to load reward group details.", {
        variant: "error",
        tag: "RewardGroupsFetchError",
      });
    }
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setSelectedGroup(null);
    setSignupCodeServerError("");
    setEmailDomainServerError("");
  };

  const handleSave = async (payload) => {
    setIsSaving(true);
    try {
      if (drawerMode === "add") {
        await service.createRewardGroup(scopeAwareFacilityID, payload);
      } else {
        await service.updateRewardGroup(
          scopeAwareFacilityID,
          selectedGroup.rewardGroupID,
          payload
        );
      }
      handleDrawerClose();
      refetch();
    } catch (err) {
      const message = getRewardGroupErrorMessage(err);
      if (message.toLowerCase().includes("signup code")) {
        setSignupCodeServerError(
          "Signup code is already in use for this facility or facility group."
        );
        return;
      }
      if (message.toLowerCase().includes("email domain")) {
        setEmailDomainServerError(
          "Email domain is already in use for this facility or facility group."
        );
        return;
      }
      enqueueSnackbar(message, {
        variant: "error",
        tag: "RewardGroupsSaveError",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleValidateSignupCode = async (signupCode, excludeRewardGroupID) => {
    setSignupCodeServerError("");
    const response = await service.checkSignupCodeAvailability(
      scopeAwareFacilityID,
      signupCode,
      excludeRewardGroupID
    );
    return response.data?.isAvailable === true;
  };

  const handleValidateEmailDomain = async (
    emailDomain,
    excludeRewardGroupID
  ) => {
    setEmailDomainServerError("");
    const response = await service.checkEmailDomainAvailability(
      scopeAwareFacilityID,
      emailDomain,
      excludeRewardGroupID
    );
    return response.data?.isAvailable === true;
  };

  const handleListDelete = (row) => {
    setDeleteTarget(row);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await service.deleteRewardGroup(
        scopeAwareFacilityID,
        deleteTarget.rewardGroupID
      );
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      refetch();
    } catch {
      enqueueSnackbar("Failed to delete reward group.", {
        variant: "error",
        tag: "RewardGroupsDeleteError",
      });
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  return (
    <Box p={2}>
      <Paper className={classes.containerPaper} elevation={0}>
        <Grid container className={classes.cardHeader}>
          <Grid item xs={8} md={8} lg={8} className={clsx("onepark-reward-groups-container", classes.headerTitle)}>
            <Typography className={classes.cardHeaderText}>
              LOYALTY REWARDS
            </Typography>
          </Grid>
          <Grid item xs={4} md={4} lg={4} className={classes.headerAddButton}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleAdd}
              className={clsx("onepark-reward-groups-container", classes.addButton)}
            >
              ADD
            </Button>
          </Grid>
        </Grid>

        <RewardGroupsList
          data={data}
          loading={loading}
          error={error}
          pagination={pagination}
          onPaginationChange={setPagination}
          onEdit={handleEdit}
          onDelete={handleListDelete}
          onRetry={refetch}
        />
      </Paper>

      <RewardGroupDrawer
        open={drawerOpen}
        onClose={handleDrawerClose}
        mode={drawerMode}
        group={selectedGroup}
        accessGroups={accessGroups}
        onSave={handleSave}
        isSaving={isSaving}
        isFirstGroup={drawerMode === "add" && data.totalCount === 0}
        onValidateSignupCode={handleValidateSignupCode}
        signupCodeServerError={signupCodeServerError}
        onValidateEmailDomain={handleValidateEmailDomain}
        emailDomainServerError={emailDomainServerError}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Delete Rewards Group</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this group?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="primary"
            onClick={handleDeleteConfirm}
          >
            Yes
          </Button>
          <Button
            variant="contained"
            onClick={handleDeleteCancel}
            className={classes.deleteDialogNoBtn}
          >
            No
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RewardGroupsContainer;
